/**
 * EmergentAuthCallbackPage — handles the hosted Emergent Auth return.
 *
 * Flow:
 *   1. User tapped "Continue with Google" on login/signup.
 *   2. Browser was redirected to auth.emergentagent.com → Google consent.
 *   3. Emergent returned the user to this page with `#session_id=<opaque>`
 *      in the URL fragment.
 *   4. We parse the fragment, post to /api/customer/auth/emergent-session,
 *      then hand the TokenResponse to the auth-store so the gated router
 *      routes to Complete Profile / Change Password / Home.
 *
 * UX: dark + gold, minimal "Signing you in..." state with a spinner. Any
 * error falls back to /customer/login with a toast.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';

export default function EmergentAuthCallbackPage() {
  const navigate = useNavigate();
  const signInWithToken = useAuthStore((s) => s.signInWithToken);
  // useRef (not state) — the exchange must fire exactly once even
  // under React StrictMode's double-invoke. Mirrors the operator-spec
  // AuthCallback pattern.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      try {
        const hash = (window.location.hash || '').replace(/^#/, '');
        const params = new URLSearchParams(hash);
        const sessionId = params.get('session_id');
        if (!sessionId) {
          toast.error('Google sign-in returned no session. Please try again.');
          navigate('/customer/login', { replace: true });
          return;
        }
        const res = await api.emergentSessionExchange(sessionId);
        // Clean the URL fragment so a refresh doesn't retry the exchange
        // against an already-consumed session_id.
        try {
          window.history.replaceState(null, '', window.location.pathname);
        } catch { /* ignore */ }
        // signInWithToken runs the gated router — Complete Profile
        // for new users, Change Password if password_is_temporary,
        // else /customer/home.
        await signInWithToken(res);
      } catch (e) {
        toast.error(e?.detail || e?.message || 'Google sign-in failed.');
        navigate('/customer/login', { replace: true });
      }
    })();
  }, [navigate, signInWithToken]);

  return (
    <div
      className="min-h-screen bg-[#0B0B0F] flex items-center justify-center px-6"
      data-testid="emergent-auth-callback"
    >
      <div className="flex flex-col items-center gap-4">
        <Loader2
          size={32}
          className="animate-spin"
          style={{ color: '#D4AF37' }}
        />
        <div className="text-white/90 text-sm font-medium">
          Signing you in with Google…
        </div>
        <div className="text-white/40 text-xs">
          Just a moment while we verify your account.
        </div>
      </div>
    </div>
  );
}
